import { useMemo } from 'react';
import {
  BarChart3, Award, ClipboardList, BookOpen,
  CheckCircle, TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';

function ScoreColor({ score }) {
  if (score >= 80) return 'text-green-600';
  if (score >= 50) return 'text-[#FFC107]';
  return 'text-red-500';
}

export function DashboardProgress({
  progressSummary,
  progressLoading,
  quizzes,
  getModuleProgress,
  setActiveTab,
}) {
  const categoryEntries = useMemo(
    () => Object.entries(progressSummary?.by_category || {}),
    [progressSummary]
  );

  const recentActivity = useMemo(
    () => progressSummary?.recent_activity || [],
    [progressSummary]
  );

  if (progressLoading) {
    return <div className="flex justify-center py-8"><div className="spinner"></div></div>;
  }

  const completion = progressSummary?.completion_percentage || 0;
  const avgScore = progressSummary?.average_score || 0;
  const completed = progressSummary?.completed_modules || 0;
  const total = progressSummary?.total_modules || 0;

  return (
    <>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="dashboard-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0A0A0A] text-sm">Overall Progress</h3>
            <BarChart3 className="w-5 h-5 text-[#002FA7]" />
          </div>
          <p className="text-3xl font-black text-[#002FA7]" data-testid="progress-completion">{completion}%</p>
          <p className="text-sm text-[#4B5563]">{completed} of {total} modules</p>
          <div className="mt-3 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
            <div className="h-full bg-[#002FA7] rounded-full transition-all duration-500" style={{ width: `${completion}%` }} />
          </div>
        </div>
        <div className="dashboard-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0A0A0A] text-sm">Average Score</h3>
            <Award className="w-5 h-5 text-[#FFC107]" />
          </div>
          <p className={`text-3xl font-black ${ScoreColor({ score: avgScore })}`} data-testid="progress-avg-score">{avgScore}%</p>
          <p className="text-sm text-[#4B5563]">across completed quizzes</p>
        </div>
        <div className="dashboard-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0A0A0A] text-sm">Quizzes Taken</h3>
            <ClipboardList className="w-5 h-5 text-[#E53935]" />
          </div>
          <p className="text-3xl font-black text-[#E53935]" data-testid="progress-quizzes-taken">{completed}</p>
          <p className="text-sm text-[#4B5563]">quizzes completed</p>
        </div>
        <div className="dashboard-widget">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#0A0A0A] text-sm">Remaining</h3>
            <BookOpen className="w-5 h-5 text-[#4B5563]" />
          </div>
          <p className="text-3xl font-black text-[#4B5563]" data-testid="progress-remaining">{total - completed}</p>
          <p className="text-sm text-[#4B5563]">modules to go</p>
        </div>
      </div>

      {/* By Category Breakdown */}
      {categoryEntries.length > 0 && (
        <div className="dashboard-widget">
          <h3 className="font-bold text-[#0A0A0A] mb-4">Progress by Category</h3>
          <div className="space-y-4">
            {categoryEntries.map(([cat, info]) => {
              const pct = info.total_modules > 0 ? Math.round((info.completed / info.total_modules) * 100) : 0;
              return (
                <div key={cat} className="p-4 border border-[#E5E7EB] rounded-sm" data-testid={`category-progress-${cat.toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-[#002FA7]/10 text-[#002FA7] px-2 py-1 rounded-sm font-medium">{cat}</span>
                      <span className="text-sm text-[#4B5563]">{info.completed} of {info.total_modules} modules completed</span>
                    </div>
                    <span className={`font-bold ${ScoreColor({ score: info.avg_score })}`}>Avg: {info.avg_score}%</span>
                  </div>
                  <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div className="h-full bg-[#002FA7] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Module-by-Module Status */}
      <div className="dashboard-widget">
        <h3 className="font-bold text-[#0A0A0A] mb-4">Module Scores</h3>
        {quizzes.length === 0 ? (
          <p className="text-[#4B5563] py-4">No modules available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {quizzes.map((quiz) => {
              const record = getModuleProgress(quiz.module);
              return (
                <div
                  key={quiz.id}
                  className={`p-4 border rounded-sm flex items-center justify-between ${record ? 'border-green-200 bg-green-50/30' : 'border-[#E5E7EB]'}`}
                  data-testid={`module-progress-${quiz.id}`}
                >
                  <div className="flex items-center gap-3">
                    {record ? (
                      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${record.score_percentage >= 80 ? 'bg-green-100' : record.score_percentage >= 50 ? 'bg-yellow-100' : 'bg-red-100'}`}>
                        <span className={`text-sm font-bold ${record.score_percentage >= 80 ? 'text-green-700' : record.score_percentage >= 50 ? 'text-yellow-700' : 'text-red-700'}`}>
                          {record.score_percentage}%
                        </span>
                      </div>
                    ) : (
                      <div className="w-10 h-10 bg-[#F3F4F6] rounded-sm flex items-center justify-center">
                        <span className="text-xs text-[#9CA3AF]">--</span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm text-[#0A0A0A]">{quiz.module}</p>
                      <p className="text-xs text-[#4B5563]">
                        {quiz.category} &middot; {quiz.questions.length} questions
                        {record && ` \u00b7 ${record.correct_answers}/${record.total_questions} correct`}
                      </p>
                    </div>
                  </div>
                  {record ? (
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-sm text-xs" onClick={() => setActiveTab('videos')}>
                      Start
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div className="dashboard-widget">
          <h3 className="font-bold text-[#0A0A0A] mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {recentActivity.map((activity) => (
              <div key={`${activity.module}-${activity.completed_at}`} className="flex items-center justify-between p-3 border border-[#E5E7EB] rounded-sm">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-sm flex items-center justify-center ${activity.score_percentage >= 80 ? 'bg-green-100' : activity.score_percentage >= 50 ? 'bg-yellow-100' : 'bg-red-100'}`}>
                    <Award className={`w-4 h-4 ${activity.score_percentage >= 80 ? 'text-green-600' : activity.score_percentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-[#0A0A0A]">{activity.module}</p>
                    <p className="text-xs text-[#4B5563]">{activity.category}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-sm ${ScoreColor({ score: activity.score_percentage })}`}>{activity.score_percentage}%</p>
                  <p className="text-xs text-[#9CA3AF]">{new Date(activity.completed_at).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {(!progressSummary || completed === 0) && (
        <div className="dashboard-widget text-center py-12">
          <TrendingUp className="w-12 h-12 text-[#002FA7]/20 mx-auto mb-4" />
          <h3 className="font-bold text-[#0A0A0A] mb-2">No Progress Yet</h3>
          <p className="text-[#4B5563] mb-6">Complete module quizzes to start tracking your progress.</p>
          <Button onClick={() => setActiveTab('videos')} className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm" data-testid="go-to-curriculum-btn">
            Go to Curriculum
          </Button>
        </div>
      )}
    </>
  );
}
